import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Optional } from '@/generic/functional';
import {
  type TrackingIndicator,
  type TrackingToast,
  TrackingIndicatorComponent,
  TrackingToastComponent,
} from '@/generic/progress';
import { useDiagnosticTracking } from '@/diagnostic/primary/DiagnosticTrackingContext.ts';

const useDiagnosticTrackingOverlay = () => {
  const tracking = useDiagnosticTracking();
  const navigate = useNavigate();
  const { t } = useTranslation('diagnostic');

  const showIndicator = tracking.isLoading && !tracking.modalVisible && tracking.failure.isEmpty();

  const goToDiagnostic = () => {
    navigate('/');
    tracking.openReport();
    tracking.dismissToast();
  };

  const goToDiagnosticAndReopen = () => {
    navigate('/');
    tracking.reopen();
    tracking.dismissToast();
  };

  const indicator: Optional<TrackingIndicator> = showIndicator
    ? Optional.of({ label: t('diagnostic.tracking.indicator'), onClick: tracking.reopen })
    : Optional.empty();

  const toast: Optional<TrackingToast> = tracking.toast.map((current) =>
    current.kind === 'success'
      ? {
          kind: 'success',
          title: t('diagnostic.tracking.toast.success.title'),
          actionLabel: t('diagnostic.tracking.toast.success.action'),
          onAction: goToDiagnostic,
          dismissLabel: t('diagnostic.tracking.toast.dismiss'),
          onDismiss: tracking.dismissToast,
        }
      : {
          kind: 'failure',
          title: t('diagnostic.tracking.toast.failure.title'),
          actionLabel: t('diagnostic.tracking.toast.failure.action'),
          onAction: goToDiagnosticAndReopen,
          dismissLabel: t('diagnostic.tracking.toast.dismiss'),
          onDismiss: tracking.dismissToast,
        },
  );

  return { indicator, toast };
};

export const DiagnosticTrackingIndicatorComponent = () => {
  const { indicator } = useDiagnosticTrackingOverlay();

  return (
    indicator
      .map((current) => (
        <TrackingIndicatorComponent
          key="indicator"
          label={current.label}
          onClick={current.onClick}
          dataSelector="diagnostic.tracking-indicator"
        />
      ))
      .orUndefined() ?? null
  );
};

export const DiagnosticTrackingToastComponent = () => {
  const { toast } = useDiagnosticTrackingOverlay();

  return (
    toast
      .map((current) => (
        <TrackingToastComponent
          key="toast"
          toast={current}
          dataSelector="diagnostic.tracking-toast"
        />
      ))
      .orUndefined() ?? null
  );
};
